import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as yup from 'yup';
import {
  BaseButton,
  BaseDialog,
  BaseDivider,
  BaseInputLabel,
  FormikField,
  BaseTextField,
  BaseTypography
} from 'src/components/common';
import { EmailTemplatesSelect } from 'src/components/fields/selects/EmailTemplatesSelect/EmailTemplatesSelect';
import { BaseDateTimePicker } from 'src/components/common/BaseDateTimePicker/BaseDateTimePicker';
import { validationErrors } from 'src/utils/validation/validationErrors';
import { validationRules } from 'src/utils/validation/validationRules';
import { IBaseForm } from 'src/utils/types/forms';
import { FormFieldsContainer } from 'src/components/common/FormFieldsGroup/FormFieldsGroup';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import {
  getDefaultEmailTemplate,
  getEmailTemplatesForSelect,
} from 'src/store/lists/emailTemplates/helpers';
import ReactPlayer from 'react-player';
import { Link, FormControl, Avatar } from '@material-ui/core';
import { RootState } from 'src/store';
import { IGuestInfo } from 'src/api/base/Guest/types';
import SunEditor, { buttonList } from 'suneditor-react';
import { BaseHelperText } from 'src/components/common/BaseTextField/BaseHelperText/BaseHelperText';
import { makeStyles } from '@material-ui/core/styles';
import { BaseButtonGroup } from '../../../../components/common/BaseButton/BaseButtonGroup';
import noUserPicture from 'src/assets/images/no-user-picture.svg';
import { BaseChip } from 'src/components/common/BaseChip/BaseChip';
import { APP_ROUTES } from 'src/routing/AppRoutes';
import { IOSSwitch } from 'src/components/common/BaseButton/IosSwitch';
import { useSnackbar } from 'notistack';

const useStyles = makeStyles((theme) => ({
  contentWrapper: {
    marginBottom: '1rem',
    maxWidth: '100%',
  },
  videoWrapper: {
    width: '100%',
    height: '100%',
    margin: '10px 0',
  },
  companyDescriptionWrapper: {
    border: '0.5px solid gainsboro',
    padding: '10px',
    borderRadius: '15px',
    whiteSpace: 'pre-wrap',
  },
  avatar: {
    width: 200,
    height: 200,
  },
  video: {
    marginBottom: 33,
  },
  avatarWidth: {
    width: 77,
    height: 77,
  },
  userData: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
	gap: '10px',
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
    },
  },
  userImgAndPicture: {
    display: 'flex',
  },
  userNameSection: {
    marginLeft: '10px',
  },
  userName: {
    fontWeight: 600,
    fontSize: '16px',
    lineHeight: '140%',
    color: theme.palette.primary.contrastText,
  },
  field: {
    [theme.breakpoints.down('sm')]: {
      marginTop: '5px',
    },
  },
  field__label: {
    fontWeight: 500,
    fontSize: '12px',
    lineHeight: '140%',
    letterSpacing: '0.03em',
    color: '#949C9E',
  },
  field__value: {
    fontWeight: 500,
    fontSize: '14px',
  },
  field__value_country: {
    fontWeight: 500,
    fontSize: '14px',
	display: 'inline-block',
  },
  field__value_language: {
	fontWeight: 500,
    fontSize: '14px',
  },
  expertises: {
    margin: '0px 0 0 -10px',
  },
  expertises__item: {
    margin: '2px 0 5px 10px',
	display: 'inline-block',
  },
  userLabel: {
	  color: '#ababab'
  },
  hr: {
	  width: 'calc(100% + 40px)',
      position: 'relative',
      marginLeft: '-20px',
	  marginTop: '20px',
      marginBottom: '0',
	  background: '#78c5f182',
	  height: '1px',
	  border: '0',
  }
}));

const Video: React.FC<{ url: string }> = (props) => {
  const { url } = props;
  
  const [canPlay, setCanPlay] = useState<boolean>(ReactPlayer.canPlay(url));

  const styles = useStyles();

  if (url.includes('vimeo.com')) {
    return (
      <>
        <Link href={url} target="_blank" rel="noreferrer">
          {url}
        </Link>
        <br />
      </>
    );
  }

  return canPlay ? (
    <ReactPlayer
      onError={(_) => setCanPlay(false)}
      url={url}
      width="100%"
      height="400px"
      className={styles.video}
    />
  ) : (
    <>
      <Link href={url} target="_blank" rel="noreferrer">
        {url}
      </Link>
      <br />
    </>
  );
};

export const OverviewGuestModalInvite: React.FC<POverviewGuestModalInvite> = (props) => {
  const { onSubmit, loading } = props;
  const defaultEmailTemplate = useSelector(getDefaultEmailTemplate);
  const { guest } = useSelector((state: RootState) => state.currentGuest);
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  
  const formik = useFormik({
    validationSchema: yup.object().shape({
      email_template_id: yup.number().required(validationErrors.REQUIRED_FIELD),
      date_interview: validationRules.requiredDefaultStringRule,
	  newTempTitle: yup.mixed().when('is_a_new_temp', {
        is: true,
        then: yup.mixed().required(validationErrors.REQUIRED_FIELD),
      }),
    }),
    initialValues: {
      email_template_id: '',
      date_interview: '',
      comment: '',
	  is_a_new_temp: false,
	  newTempSec: false,
	  newTempTitle: '',
    },
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: (values) => {
      onSubmit(values);
	  
	  const hash = window.location.hash.replace('#', '');
		const params = new URLSearchParams(hash);
		const guestId = params.get('guest');
		if(guestId){
			history.push(APP_ROUTES.MAIN);
		} else {
			setTimeout(() => {
				enqueueSnackbar('The request for an interview has been successfully sent.', { variant: 'success' });
			}, 4000);
		}
    },
  });

  const { email_template_id } = formik.values;
  const { setFieldValue } = formik;
  useEffect(() => {
    if (defaultEmailTemplate && !email_template_id) {
      setFieldValue('email_template_id', defaultEmailTemplate.id);
    }
  }, [defaultEmailTemplate, setFieldValue, email_template_id]);
  const emailTemplates = useSelector(getEmailTemplatesForSelect);
  const templateValue = emailTemplates.find(
    (item) => item.value === email_template_id,
  );

  useEffect(() => {
    if (templateValue) {
      formik.setFieldValue('comment', templateValue.content);
    }
  }, [templateValue]);

  return (
  <>
      <form onSubmit={formik.handleSubmit}>
          <FormFieldsContainer>
            <div className='form-input-section'>

              <div className='w-70 email'>
                <FormikField
                  label={
                    <div className="input-label">
                      Invite using this email template:
                    </div>
                  }
                  name="email_template_id"
                  formik={formik}
                  inputComponent={EmailTemplatesSelect}
                  onChange={(newValue: number) => {
                    formik.setFieldValue('email_template_id', newValue);
                  }}
                />
              </div>

              <div className='w-30 interview date'>
                <BaseDateTimePicker
                  label={
                    <div className="interview input-label">
                      Invite for interview on:
                    </div>
                  }
                  value={formik.values.date_interview}
                  errorText={formik.errors.date_interview}
                  onChange={(value: any) => {
                    formik.setFieldValue('date_interview', value);
                  }}
                  disablePast
                  startPosition
                />
              </div>
            </div>
            <FormControl error={Boolean(formik.errors.comment)}>

              <div className="input-label">
                Question:
              </div>
              {/* <BaseInputLabel>Question:</BaseInputLabel> */}
              <SunEditor
                setDefaultStyle="font-size: 16px; font-family: IBM Plex Sans;"
                showToolbar
                name="comment"
                setContents={
                  templateValue ? templateValue.content : formik.values.comment
                }
                onBlur={(e, editorContent) => {
                  formik.setFieldValue('comment', editorContent);
                  formik.setFieldValue('newTempSec', true);
                }}
                setOptions={{
                  height: 200,
                  fontSize: [36],
                  buttonList: [
                    ...buttonList.formatting,
                    ['font', 'align', 'lineHeight'],
                  ],
                  // Other option
                }}
              />
              <BaseHelperText>{formik.errors.comment}</BaseHelperText>
            </FormControl>
            {/* {formik.values.newTempSec && (
              <>
                <BaseButtonGroup
                  value={formik.values.is_a_new_temp}
                  label="Do you want to save the changes as a new template?"
                  onChange={(value) => {
                    formik.setFieldValue('is_a_new_temp', value);
                  }}
                />
                {formik.values.is_a_new_temp && (
                  <>
                    <div>
                      <BaseTextField
                        fullWidth
                        value={formik.values.newTempTitle}
                        errorText={formik.errors.newTempTitle}
                        onChange={formik.handleChange}
                        name="newTempTitle"
                        label="Enter the name of the new template"
                      />
                    </div>
                  </>
                )}
              </>
            )} */}

            <div className='create-new-template'>

              <div className='w-60 label-switch'>
                <BaseTypography variant="caption" className="mt-8">
                  Do you want to save the changes as a new template?<span><IOSSwitch name="is_a_new_temp" checked={formik.values.is_a_new_temp} onChange={formik.handleChange} /></span>
                </BaseTypography>
              </div>

              <div className='w-20 invite'><BaseButton type="submit" fullWidth loading={loading}>
                Invite Guest
              </BaseButton></div>

            </div>
            {formik.values.is_a_new_temp && <div>
              <BaseTextField
                fullWidth
                value={formik.values.newTempTitle}
                errorText={formik.errors.newTempTitle}
                onChange={formik.handleChange}
                name="newTempTitle"
                label="Enter the name of the new template"
              />
            </div>}
          </FormFieldsContainer>
        </form>
    </>
  );
};
